/**
 * `seasHits` on the chart (`docs/plans/seasonality-layout.md`, owner 2026-10-22):
 * the server sends one shape per block — its stacked `level` lines are drawn
 * by `geometry-annotations.ts` like any pattern's — and this is the rest: the
 * pane's scale, which is a count of years, and the legend's reading of the
 * block under the cursor. Pure.
 */

import type { Pane, Plot } from "@qufolio/model";
import type { GeometryHits, GeometryPayload } from "@qufolio/transport";

export function isSeasHitsAlias(alias: string | undefined): boolean {
  return (alias ?? "").split("_", 1)[0] === "seasHits";
}

function hitsPlots(plots: readonly Plot[] | undefined): Plot[] {
  return (plots ?? []).filter((p) => p.type === "geometry" && !p.hidden && isSeasHitsAlias(p.outputAlias));
}

/** Every block of a pane's `seasHits` plots: the time it covers and its numbers. */
function blocks(geometry: GeometryPayload | null, plots: readonly Plot[] | undefined) {
  if (!geometry) return [];
  return hitsPlots(plots).flatMap((plot) =>
    (geometry.sets[plot.outputAlias!] ?? []).flatMap((shape) => {
      const first = shape.primitives[0];
      if (!shape.hits || !first || first.t !== "level") return [];
      return [{ from: first.from, to: first.to ?? first.from, hits: shape.hits }];
    }),
  );
}

/**
 * The pane's Y range, ±(most years in a block) with half a unit of room, or
 * `undefined` when there is nothing to draw. A count does not fit like a price:
 * zero belongs in the middle, and a range fitted to one quiet stretch would
 * rescale every time you pan.
 */
export function hitsRange(
  geometry: GeometryPayload | null,
  plots: readonly Plot[] | undefined,
): { min: number; max: number } | undefined {
  let most = 0;
  for (const b of blocks(geometry, plots)) most = Math.max(most, b.hits.up, b.hits.down);
  if (most === 0) return undefined;
  return { min: -(most + 0.5), max: most + 0.5 };
}

/** The axis a pane's `seasHits` plot is drawn on — its own, else the pane's first. */
export function hitsAxisId(pane: Pane): string | undefined {
  const plot = hitsPlots(pane.plots)[0];
  if (!plot) return undefined;
  return plot.axisRef ?? pane.axes?.[0]?.id;
}

const pct = (n: number) => `${n > 0 ? "+" : ""}${n.toFixed(2)}%`;

/**
 * The legend's line for the block holding `time` (owner, 2026-10-22):
 * `4 up / 6 down · Xs 3/10 · Sum +5.00% / -6.30% (Δ -1.30%) · Avg win +1.25% / loss -1.05% (Δ +0.20%)`.
 */
export function hitsReading(hits: GeometryHits): string {
  return (
    `${hits.up} up / ${hits.down} down · Xs ${hits.xs}/${hits.years} · ` +
    `Sum ${pct(hits.sumPos)} / ${pct(hits.sumNeg)} (Δ ${pct(hits.sumDelta)}) · ` +
    `Avg win ${pct(hits.avgWin)} / loss ${pct(hits.avgLoss)} (Δ ${pct(hits.avgDelta)})`
  );
}

export function hitsAt(
  geometry: GeometryPayload | null,
  plots: readonly Plot[] | undefined,
  time: number | undefined,
): string {
  if (time === undefined) return "";
  const found = blocks(geometry, plots).find((b) => b.from <= time && time <= b.to);
  return found ? hitsReading(found.hits) : "";
}
